import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ROUTES } from '@/shared/config/routes';

import { getChatInfo, useCurrentAgentId } from '@/features/chats';
import {
  SessionsLayout,
  type SessionsAppDeps,
  type SessionsAppShellDeps,
  type SessionsChatsDeps,
  type SessionsInterviewDeps,
  type SessionsLayoutDeps,
  type InterviewPersonaLike,
} from '@/features/sessions';
import {
  useCloseSidePanel,
  useIsSidePanelOpen,
  useLastNonNullLayoutMode,
  useSidePanelLayoutMode,
  useSidePanelType,
} from '@/app/store/ui';
import { AppSidebar } from '@/widgets/app-shell';
import { usePersonas } from '@/entities/persona';
import { personaLabel } from '@/shared/lib/persona-label';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import {
  InterviewPersonasPanel,
  InterviewWelcomeMessage,
  DownloadDropdown,
  useInterviewDownload,
  useSaveSessionToProject,
} from '@/features/audience-interview';
import { useChatId } from '@/features/chats';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { PersonaDetailV2 } from '@/features/audience-flow-widget';
import { getChatActions } from '@/lib/chat-actions/chat-actions-bindings';
import { mapPersonaToInterviewPersona } from './mapPersonaToInterviewPersona';
import { useSelectedPersonaIdFromChatAdapter } from './useSelectedPersonaIdFromChatAdapter';

function usePersonasAdapter(workspaceId: string | null | undefined, enabled = true) {
  const { data, isLoading } = usePersonas(workspaceId, enabled);
  const mapped = useMemo(() => data?.map(mapPersonaToInterviewPersona), [data]);
  return { data: mapped, isLoading };
}

function useFullPersonasAdapter(workspaceId: string | null | undefined, enabled = true) {
  return usePersonas(workspaceId, enabled);
}

function useInterviewDownloadAdapter(persona: InterviewPersonaLike | null) {
  const contextId = useChatId();
  const workspaceId = useCurrentWorkspaceId();

  const { download, isDownloading } = useInterviewDownload({
    persona,
    contextId,
    workspaceId,
  });

  return { download, isDownloading };
}

/**
 * Dependency-injection wiring for `SessionsLayout`.
 *
 * `SessionsLayout` is a route-element layout that wraps the chat-session
 * routes (`Root` + `StreamChatSession`). To keep `features/sessions` free
 * of cross-feature OUT (chats, agents) and cross-app OUT (`@/app/store/*`),
 * this wrapper absorbs the deps and threads them through `<SessionsLayout
 * deps={...} />`.
 *
 * Mirrors the dependency-injection pattern used by `pages/Root` and
 * `pages/StreamChatSession` for `ChatSessionLayout` (chats slice).
 *
 * The wiring lives at `@/app/routes/` alongside `AppRoutes.tsx` because
 * `SessionsLayout` is mounted as a route element — there is no single
 * "page" that owns it.
 */
export const SessionsLayoutRoute = () => {
  const navigate = useNavigate();

  const chatsDeps = useMemo<SessionsChatsDeps>(
    () => ({
      useCurrentAgentIdHook: useCurrentAgentId,
      useChatIdHook: useChatId,
      getChatInfo,
    }),
    [],
  );

  const appDeps = useMemo<SessionsAppDeps>(
    () => ({
      useIsSidePanelOpenHook: useIsSidePanelOpen,
      useSidePanelTypeHook: useSidePanelType,
      useSidePanelLayoutModeHook: useSidePanelLayoutMode,
      useCloseSidePanelHook: useCloseSidePanel,
      useLastNonNullLayoutModeHook: useLastNonNullLayoutMode,
    }),
    [],
  );

  const appShellDeps = useMemo<SessionsAppShellDeps>(
    () => ({
      AppSidebar,
    }),
    [],
  );

  const handlePersonaSelect = useCallback(
    (persona: InterviewPersonaLike) => {
      const { sendOrCreateChat } = getChatActions();
      const personaToTalkTo = personaLabel(persona);
      void sendOrCreateChat(navigate, {
        agentId: SPECIAL_AGENT_IDS.AUDIENCE_FLOW,
        prompt: `I want to speak with ${personaToTalkTo}`,
        isHidden: true,
        inputs: {
          selectedPersonaId: persona.id,
        },
      });
    },
    [navigate],
  );

  const handleNavigateBack = useCallback(() => {
    void navigate(ROUTES.KNOWLEDGE_HUB_AUDIENCES);
  }, [navigate]);

  const interviewDeps = useMemo<SessionsInterviewDeps>(
    () => ({
      InterviewPanel: InterviewPersonasPanel,
      PersonaDetail: PersonaDetailV2,
      DownloadDropdown,
      useSaveSession: useSaveSessionToProject,
      WelcomeMessage: InterviewWelcomeMessage,
      usePersonas: usePersonasAdapter,
      useFullPersonas: useFullPersonasAdapter,
      onPersonaSelect: handlePersonaSelect,
      onNavigateBack: handleNavigateBack,
      useDownload: useInterviewDownloadAdapter,
      useSelectedPersonaIdFromChat: useSelectedPersonaIdFromChatAdapter,
    }),
    [handlePersonaSelect, handleNavigateBack],
  );

  const deps = useMemo<SessionsLayoutDeps>(
    () => ({ chats: chatsDeps, app: appDeps, appShell: appShellDeps, interview: interviewDeps }),
    [chatsDeps, appDeps, appShellDeps, interviewDeps],
  );

  return <SessionsLayout deps={deps} />;
};
